import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  StatusPill,
  BadgeSoft,
  MetricBar,
  Sparkline,
  Delta,
  InfoPopover,
  Table,
  TableHead,
  TableRow,
} from "@/components/vb";

/** Buyer · Signals · Visibility · Share of voice · Trend · Latest run · Open. */
const BUYER_COLS = "minmax(0,2.1fr) 74px 1fr 1.15fr 88px minmax(0,1.55fr) 64px";
import { TRUST_SIGNALS_TOTAL } from "@/types/vb";
import type { Buyer } from "@/types/vb";
import { METRIC_INFO } from "./metricCopy";

/** Format a 0–1 fraction as a whole-percent string ("64%"). */
function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

/** Relative time ("2 days ago"), guarding against a malformed timestamp so one
 *  bad row can't crash the whole list (formatDistanceToNow throws on NaN). */
function relativeTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatDistanceToNow(d, { addSuffix: true });
}

/** Provenance tag: EDITED when the persona was changed from the suggestion,
 *  else SUGGESTED. Mirrors the prototype's `.tag-soft` on the buyer name. */
function ProvenanceTag({ buyer }: { buyer: Buyer }) {
  const edited = buyer.edited_fields.length > 0;
  return (
    <BadgeSoft tone={edited ? "edited" : "suggested"}>
      {edited ? "Edited" : "Suggested"}
    </BadgeSoft>
  );
}

function BuyerRow({ buyer }: { buyer: Buyer }) {
  const navigate = useNavigate();
  const href = `/virtual-buyer/${buyer.id}`;

  const latest = buyer.latest_analysis;
  const m = buyer.metrics;
  const hasMetrics = m != null;
  const ranWhen = relativeTime(latest?.completed_at ?? null);

  // The whole row is a mouse-click convenience; the real, keyboard-accessible
  // navigation control is the <Link> in the last cell (focusable, announced,
  // middle-click / open-in-new-tab work). Ignore clicks that originate on the
  // link so it isn't navigated twice.
  const onRowClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("a")) return;
    // Don't navigate when the click was actually a text selection (drag).
    if (!window.getSelection()?.isCollapsed) return;
    navigate(href);
  };

  return (
    <TableRow interactive onClick={onRowClick}>
      {/* Buyer */}
      <span className="vb-b-name">
        <span className="vb-b-name__nm">
          <span className="vb-b-name__t">{buyer.name}</span>
          <ProvenanceTag buyer={buyer} />
        </span>
        <span className="vb-b-name__d">
          {buyer.company?.name ? `${buyer.company.name} · ` : ""}
          {buyer.description}
        </span>
      </span>

      {/* Signals exposed (data-label drives the mobile card layout) */}
      {hasMetrics && m.signals_exposed != null ? (
        <span className="vb-b-sig tnum" data-label="Signals">
          {m.signals_exposed}/{TRUST_SIGNALS_TOTAL}
          <Delta value={m.signals_exposed_delta} />
        </span>
      ) : (
        <span className="vb-cell-empty" data-label="Signals">-</span>
      )}

      {/* Visibility + trend delta */}
      {hasMetrics && m.visibility != null ? (
        <span className="vb-b-vis" data-label="Visibility">
          <span className="vb-b-vis__v tnum">{pct(m.visibility)}</span>
          <Delta value={m.visibility_delta == null ? null : Math.round(m.visibility_delta * 100)} />
        </span>
      ) : (
        <span className="vb-cell-empty" data-label="Visibility">-</span>
      )}

      {/* Share of voice */}
      {hasMetrics && m.sov != null ? (
        <span className="vb-b-sov" data-label="Share of voice">
          <MetricBar value={m.sov} />
          <span className="vb-b-sov__pct tnum">{pct(m.sov)}</span>
        </span>
      ) : (
        <span className="vb-cell-empty" data-label="Share of voice">-</span>
      )}

      {/* Trend sparkline */}
      {hasMetrics && m.visibility_history.length >= 2 ? (
        <span className="vb-trend-cell" data-label="Trend">
          <Sparkline data={m.visibility_history} />
        </span>
      ) : (
        <span className="vb-cell-empty" data-label="Trend">-</span>
      )}

      {/* Latest run */}
      <span className="vb-b-run" data-label="Latest run">
        {latest ? (
          <>
            <StatusPill status={latest.status} />
            {ranWhen && <span className="vb-b-run__meta">{ranWhen}</span>}
          </>
        ) : (
          <span className="vb-b-run__meta">No runs yet</span>
        )}
      </span>

      {/* Open - the row's accessible navigation control */}
      <Link className="vb-open-link" to={href} aria-label={`Open ${buyer.name}`}>
        Open
        <ArrowRight size={13} aria-hidden="true" />
      </Link>
    </TableRow>
  );
}

/** VB-A buyers overview table. Faithful to the prototype's `.dt.buyers`:
 *  Buyer · Signals · Visibility · Share of voice · Trend · Latest run · Open. */
export function BuyerListTable({ buyers }: { buyers: Buyer[] }) {
  // Styled CSS-grid list, not a semantic <table> (converting would break the
  // grid). We deliberately don't add role="table"/"row"/"cell" - partial
  // table ARIA is worse than none; screen readers read the cells in order.
  return (
    <Table columns={BUYER_COLS} minWidth={680}>
      <TableHead>
        <span>Buyer</span>
        <span className="vb-th">Signals <InfoPopover label="About signals exposed">{METRIC_INFO.signals}</InfoPopover></span>
        <span className="vb-th">Visibility <InfoPopover label="About visibility">{METRIC_INFO.visibility}</InfoPopover></span>
        <span className="vb-th">Share of voice <InfoPopover label="About share of voice">{METRIC_INFO.sov}</InfoPopover></span>
        <span className="vb-th">Trend <InfoPopover label="About trend">{METRIC_INFO.trend}</InfoPopover></span>
        <span>Latest run</span>
        <span aria-hidden="true" />
      </TableHead>
      {buyers.map((b) => (
        <BuyerRow key={b.id} buyer={b} />
      ))}
    </Table>
  );
}

export default BuyerListTable;
